/**
 * Global error boundary: any uncaught render error or failed lazy-chunk
 * download in the app shows a recoverable card instead of a white screen.
 *
 * Chunk failures after a deploy (old index still referencing removed hashes,
 * long-lived Telegram WebView) are retried automatically ONCE per pageload;
 * everything else offers a manual "Повторить" and "Назад".
 */
import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}
interface State {
  error: Error | null
}

export default class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  override componentDidCatch(error: Error) {
    // Never swallow the error: it stays in the browser console for diagnosis.
    console.error('[AppErrorBoundary]', error)
    const chunkFailure = /Loading chunk \d+ failed|error loading dynamically imported module|Importing a module script failed/i.test(
      String(error?.message ?? error),
    )
    if (chunkFailure) {
      try {
        if (sessionStorage.getItem('app_chunk_retried') !== '1') {
          sessionStorage.setItem('app_chunk_retried', '1')
          window.location.reload()
        }
      } catch {
        /* storage unavailable — show the card below */
      }
    }
  }

  override render() {
    if (!this.state.error) return this.props.children
    const msg = String(this.state.error?.message ?? '')
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          padding: 'max(env(safe-area-inset-top), 16px) 16px max(env(safe-area-inset-bottom), 16px)',
          background: 'var(--color-background)',
          color: 'var(--color-text)',
        }}
      >
        <div className="card" style={{ maxWidth: 420, width: '100%', padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 40, lineHeight: 1 }}>⚠️</div>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: '12px 0 8px' }}>Не удалось открыть страницу</h1>
          <p style={{ fontSize: 14, color: 'var(--color-ash)', lineHeight: 1.55, margin: '0 0 16px' }}>
            Произошла ошибка при загрузке. Нажмите «Повторить» — обычно это помогает.
          </p>
          <button
            type="button"
            className="btn btn-acid"
            style={{ width: '100%' }}
            onClick={() => window.location.reload()}
          >
            Повторить
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ width: '100%', marginTop: 8 }}
            onClick={() => window.history.back()}
          >
            Назад
          </button>
          {msg && (
            <p style={{ fontSize: 11, color: 'var(--color-fog)', marginTop: 12, wordBreak: 'break-word' }}>
              {msg.slice(0, 180)}
            </p>
          )}
        </div>
      </div>
    )
  }
}
